import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';

/** A breadcrumb hop under the console base. Omit `slug` to mark the current page. */
export interface LiveCrumb {
  label: ReactNode;
  /** Path segment under `/services/live/:serviceId`; omit for the current (non-link) crumb. */
  slug?: string;
}

const ROOT_LABEL = 'Live Transcoding';

/**
 * Consistent breadcrumb trail for every service-scoped Live Transcoding page,
 * shown in the PageHeader's small text above the title. Always rooted at the
 * service dashboard ("Live Transcoding"); pass the sub-page hops via `trail`.
 * The dashboard itself passes no trail (root renders as the current page).
 */
export function LiveConsoleBreadcrumbs({ trail = [] }: { trail?: LiveCrumb[] }) {
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/live/${serviceId}`;

  const items = [
    trail.length === 0 ? (
      <BreadcrumbItem key="root" current>{ROOT_LABEL}</BreadcrumbItem>
    ) : (
      <BreadcrumbItem key="root" asChild>
        <Link to={base}>{ROOT_LABEL}</Link>
      </BreadcrumbItem>
    ),
    ...trail.map((crumb, i) =>
      crumb.slug ? (
        <BreadcrumbItem key={i} asChild>
          <Link to={`${base}/${crumb.slug}`}>{crumb.label}</Link>
        </BreadcrumbItem>
      ) : (
        <BreadcrumbItem key={i} current>
          {crumb.label}
        </BreadcrumbItem>
      ),
    ),
  ];

  return <Breadcrumbs>{items}</Breadcrumbs>;
}
